import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';

import apiClient from '../../utils/apiClient';

export interface LocationType {
  id: number;
  name: string;
  streetAddress: string;
  addressLine2?: string;
  city: string;
  state: string;
  country: string;
  zip: string;
  coordinates: { lat: number; lng: number } | string;
  taxRate?: number | string | null;
  billingContactEmail?: string | string[] | null;
  isEMAILNotificationsEnabled?: boolean;
  settlementAccount?: { id: number } | null;
  timezone?: string;
  strategy?: string;
}

interface V2StoreState {
  locations: LocationType[];

  locationsLoading: Record<number, boolean>;
  locationsErrors: Record<number, string | null>;

  isFetchingAll: boolean;
  fetchAllError: string | null;
}

interface V2StoreActions {
  fetchLocation: (locationId: number) => Promise<void>;
  fetchAllLocations: () => Promise<void>;
  refreshLocation: (locationId: number) => Promise<void>;
  clearAll: () => void;
  clearLocationState: (locationId: number) => void;
}

type V2Store = V2StoreState & V2StoreActions;

const initialState: V2StoreState = {
  locations: [],
  locationsLoading: {},
  locationsErrors: {},
  isFetchingAll: false,
  fetchAllError: null,
};

export const useV2Store = create<V2Store>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        fetchAllLocations: async () => {
          set(initialState);
          if (get().isFetchingAll) {
            console.warn('Already fetching all locations.');
            return;
          }

          set({ isFetchingAll: true, fetchAllError: null });

          try {
            const locationsData: LocationType[] = await apiClient.get('/locations');

            set(state => ({
              locations: locationsData,
              isFetchingAll: false,
            }));
          } catch (error: any) {
            console.error("Failed to fetch all locations:", error);
            set({
              isFetchingAll: false,
              fetchAllError: error?.message || 'Failed to fetch locations',
            });
          }
        },

        fetchLocation: async (locationId) => {
          if (get().locationsLoading[locationId]) {
            console.warn(`Already fetching location ${locationId}.`);
            return;
          }

          set(state => ({
            locationsLoading: { ...state.locationsLoading, [locationId]: true },
            locationsErrors: { ...state.locationsErrors, [locationId]: null },
          }));

          try {
            const location = await apiClient.get(`/locations/${locationId}`);
            if (!location) {
              throw new Error(`Location with ID ${locationId} not found.`);
            }

            set(state => ({
              locations: state.locations.some(loc => loc.id === location.id)
                ? state.locations.map(loc => loc.id === location.id ? location : loc)
                : [...state.locations, location],
              locationsLoading: { ...state.locationsLoading, [locationId]: false },
            }));
          } catch (error: any) {
            console.error(`Failed to fetch location ${locationId}:`, error);
            set(state => ({
              locationsErrors: { ...state.locationsErrors, [locationId]: error?.message || 'Failed to fetch location' },
              locationsLoading: { ...state.locationsLoading, [locationId]: false },
            }));
          }
        },

        refreshLocation: async (locationId) => {
          set(state => ({
            locationsLoading: { ...state.locationsLoading, [locationId]: true },
            locationsErrors: { ...state.locationsErrors, [locationId]: null },
          }));

          try {
            const location = await apiClient.get(`/locations/${locationId}`);
            if (!location) {
              throw new Error(`Location with ID ${locationId} not found.`);
            }

            set(state => ({
              locations: state.locations.some(loc => loc.id === location.id)
                ? state.locations.map(loc => loc.id === location.id ? location : loc)
                : [...state.locations, location],
              locationsLoading: { ...state.locationsLoading, [locationId]: false },
            }));
          } catch (error: any) {
            console.error(`Failed to refresh location ${locationId}:`, error);
            set(state => ({
              locationsErrors: { ...state.locationsErrors, [locationId]: error?.message || 'Failed to refresh location' },
              locationsLoading: { ...state.locationsLoading, [locationId]: false },
            }));
          }
        },

        clearAll: () => {
          set(initialState);
        },

        clearLocationState: (locationId) => {
          set(state => ({
            locationsLoading: { ...state.locationsLoading, [locationId]: false },
            locationsErrors: { ...state.locationsErrors, [locationId]: null },
          }));
        }

      }),
      {
        name: 'v2-store',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          locations: state.locations,
        }),
        version: 1,
      }
    ),
    {
      name: 'V2Store',
      enabled: process.env.NODE_ENV !== 'production',
    }
  )
);
